import { useState, useEffect } from "react";

// ── Quiplash Player Controller ────────────────────────────────────────────────
// Props:
//   prompt      : string | null
//   myAnswer    : string | null
//   answered    : bool
//   voteData    : { prompt, answers } | null
//   myVote      : string | null      — opaque option id the player voted for
//   voted       : bool
//   result      : object | null
//   timeLeft    : number             — server-synced time (resets on phase change)
//   playerName  : string
//   onAnswer    : (text: string) => void
//   onVote      : (votedFor: string) => void

export default function QuiplashPlayer({ prompt, myAnswer, answered, voteData, myVote, voted, result, timeLeft, playerName, onAnswer, onVote }) {
  const [inputText, setInputText] = useState("");
  const [localTime, setLocalTime] = useState(timeLeft);

  // Reset input when new prompt arrives
  useEffect(() => { setInputText(""); }, [prompt]);

  // Sync timer from server on phase changes
  useEffect(() => { setLocalTime(timeLeft); }, [timeLeft, prompt, voteData]);

  // Tick down locally
  useEffect(() => {
    if (result) return;
    const iv = setInterval(() => setLocalTime(t => Math.max(0, t - 1)), 1000);
    return () => clearInterval(iv);
  }, [prompt, voteData, result]);

  const timerColor = localTime > 20 ? "#3dffa0" : localTime > 10 ? "#ffe600" : "#ff3355";

  // Phase 3: results
  if (result) {
    const vc = result.vote_counts || {};
    const myVotes = vc[playerName] || 0;
    const entries = Object.entries(result.answers || {});
    const sorted = [...entries].sort((a, b) => (vc[b[0]] || 0) - (vc[a[0]] || 0));
    const iWon = sorted.length > 0 && sorted[0][0] === playerName && myVotes > 0;
    return (
      <div className="flex-1 flex flex-col gap-3 animate-slide-up">
        <div style={{
          background: "#001f33", border: "2.5px solid #000", borderRadius: "1rem",
          boxShadow: "4px 4px 0 #000", padding:"var(--pad-sm) var(--pad-md)", textAlign: "center",
        }}>
          <div className="font-heading text-[#aaa]" style={{ fontSize:"var(--fs-sm)", marginBottom: "0.25rem" }}>Фраза</div>
          <div className="font-heading text-white" style={{ fontSize:"var(--fs-md)", lineHeight: 1.3 }}>{result.prompt}</div>
        </div>
        {iWon && (
          <div className="text-center animate-bounce-in">
            <span style={{ fontSize:"var(--fs-3xl)" }}>🏆</span>
            <p className="font-display text-[#ffe600]" style={{ fontSize:"var(--fs-lg)" }}>Твой ответ победил!</p>
          </div>
        )}
        {myVotes > 0 && !iWon && (
          <p className="font-display text-center text-[#00DDFF]" style={{ fontSize:"var(--fs-md)" }}>
            За тебя проголосовали: {myVotes} раз
          </p>
        )}
        <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap:"var(--gap-md)" }}>
          {sorted.map(([author, text], i) => {
            const isMe = author === playerName;
            const votes = vc[author] || 0;
            return (
              <div key={author} style={{
                background: isMe ? "#001a0f" : "#1a1030",
                border: `2px solid ${i === 0 && votes > 0 ? "#ffe600" : isMe ? "#3dffa0" : "#333"}`,
                borderRadius: "0.75rem", padding:"var(--pad-sm) var(--pad-md)",
                display: "flex", alignItems: "center", gap:"var(--gap-md)",
              }}>
                <div style={{ flex: 1 }}>
                  <div className="font-heading text-white" style={{ fontSize:"var(--fs-sm)" }}>{text}</div>
                  <div className="font-heading" style={{ fontSize:"var(--fs-xs)", color: isMe ? "#3dffa0" : "#888", marginTop: "0.1rem" }}>
                    — {isMe ? "ты" : author}
                  </div>
                </div>
                {votes > 0 && (
                  <span className="font-display" style={{ color: "#ffe600", fontSize:"var(--fs-lg)" }}>{votes}🗳️</span>
                )}
              </div>
            );
          })}
        </div>
        <p className="font-heading text-center text-[#555]" style={{ fontSize:"var(--fs-sm)" }}>
          Следующий раунд начнётся автоматически...
        </p>
      </div>
    );
  }

  // Phase 2: voting
  if (voteData) {
    const othersEntries = Object.entries(voteData.answers || {});

    if (voted) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 animate-slide-up">
          <div style={{ fontSize: "3rem" }}>✅</div>
          <p className="font-display text-white text-xl">Голос принят!</p>
          {myVote && (
            <div style={{
              background: "#001f33", border: "2.5px solid #00DDFF",
              borderRadius: "1rem", padding: "0.75rem 1.5rem", textAlign: "center",
            }}>
              <p className="font-heading text-[#aaa]" style={{ fontSize:"var(--fs-sm)" }}>Ты проголосовал за:</p>
              <p className="font-heading text-white" style={{ fontSize:"var(--fs-md)" }}>{voteData.answers[myVote]}</p>
            </div>
          )}
          <p className="font-heading text-[#555]" style={{ fontSize:"var(--fs-sm)" }}>Ждём остальных...</p>
        </div>
      );
    }

    return (
      <div className="flex-1 flex flex-col gap-3 animate-slide-up">
        <div style={{
          background: "#001f33", border: "2.5px solid #000", borderRadius: "1rem",
          boxShadow: "4px 4px 0 #000", padding:"var(--pad-sm) var(--pad-md)", textAlign: "center",
        }}>
          <div className="font-heading text-[#00DDFF]" style={{ fontSize:"var(--fs-xs)", marginBottom: "0.2rem" }}>🗳️ Голосуй за лучший ответ</div>
          <div className="font-heading text-white" style={{ fontSize:"var(--fs-md)", lineHeight: 1.3 }}>{voteData.prompt}</div>
        </div>
        <div className="font-heading text-center" style={{ fontSize:"var(--fs-sm)", color: timerColor }}>⏱ {localTime}с</div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap:"var(--gap-md)" }}>
          {othersEntries.map(([optionId, text], i) => (
            <button
              key={optionId}
              onClick={() => onVote(optionId)}
              style={{
                background: "#1a1030", border: "2.5px solid #000",
                borderRadius: "0.85rem", boxShadow: "3px 3px 0 #000",
                padding:"var(--pad-sm) var(--pad-md)", textAlign: "left", cursor: "pointer",
                display: "flex", alignItems: "center", gap:"var(--gap-md)",
                transition: "transform 0.1s",
              }}
              onMouseDown={e => e.currentTarget.style.transform = "translate(2px,2px)"}
              onMouseUp={e => e.currentTarget.style.transform = ""}
            >
              <span className="font-display" style={{ color: "#00DDFF", fontSize:"var(--fs-lg)", minWidth: "1.2rem" }}>{i + 1}</span>
              <span className="font-heading text-white" style={{ fontSize:"var(--fs-md)", flex: 1, lineHeight: 1.3 }}>{text}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Phase 1: answer input
  if (answered) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 animate-slide-up">
        <div style={{ fontSize: "3rem" }}>✍️</div>
        <p className="font-display text-white text-xl">Ответ отправлен!</p>
        <div style={{
          background: "#001f33", border: "2.5px solid #00DDFF",
          borderRadius: "1rem", padding: "0.75rem 1.5rem", textAlign: "center", maxWidth: "90%",
        }}>
          <p className="font-heading text-[#aaa]" style={{ fontSize:"var(--fs-sm)" }}>Твой ответ:</p>
          <p className="font-heading text-white" style={{ fontSize:"var(--fs-md)" }}>{myAnswer}</p>
        </div>
        <p className="font-heading text-[#555]" style={{ fontSize:"var(--fs-sm)" }}>Ждём остальных...</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col gap-3 animate-slide-up">
      <div style={{
        background: "#001f33", border: "2.5px solid #000", borderRadius: "1rem",
        boxShadow: "4px 4px 0 #000", padding:"var(--pad-sm) var(--pad-md)",
      }}>
        <div className="font-heading text-[#00DDFF]" style={{ fontSize:"var(--fs-xs)", marginBottom: "0.25rem" }}>
          💬 Закончи фразу
        </div>
        <div className="font-heading text-white" style={{ fontSize:"var(--fs-md)", lineHeight: 1.4 }}>
          {prompt || "Загрузка..."}
        </div>
      </div>

      <div className="flex justify-between items-center">
        <span className="font-heading text-[#aaa]" style={{ fontSize:"var(--fs-sm)" }}>Осталось:</span>
        <span className="font-display" style={{ color: timerColor, fontSize:"var(--fs-xl)", animation: localTime <= 10 ? "wiggle .4s infinite" : "none" }}>
          ⏱ {localTime}с
        </span>
      </div>

      <textarea
        value={inputText}
        onChange={e => setInputText(e.target.value.slice(0, 200))}
        placeholder="Напиши свой ответ..."
        maxLength={200}
        rows={3}
        style={{
          width: "100%", background: "#1a1030",
          border: "2.5px solid #000", borderRadius: "0.85rem",
          boxShadow: "inset 2px 2px 0 rgba(0,0,0,0.5)",
          color: "#fff", fontFamily: "'Nunito',sans-serif", fontSize:"var(--fs-md)",
          padding:"var(--pad-sm) var(--pad-md)", resize: "none", outline: "none", lineHeight: 1.4,
        }}
      />
      <div className="font-heading text-right text-[#555]" style={{ fontSize:"var(--fs-xs)", marginTop: "-0.5rem" }}>
        {inputText.length}/200
      </div>

      <button
        onClick={() => { if (inputText.trim()) onAnswer(inputText.trim()); }}
        disabled={!inputText.trim()}
        className="btn-cartoon w-full"
        style={{
          background: inputText.trim() ? "#00DDFF" : "#333",
          color: inputText.trim() ? "#000" : "#666",
          fontSize:"var(--fs-lg)", padding:"var(--pad-md)",
          cursor: inputText.trim() ? "pointer" : "not-allowed",
        }}
      >
        Отправить ответ ✍️
      </button>
    </div>
  );
}
